import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import { useTheme } from '@evi/skins';
import { fetchMyOrders, formatCents, type MyStoreOrder, type MyStoreOrderStatus } from '@/lib/api';
import styles from './Store.module.css';

interface Props { onNavigate: (path: string) => void }

/** Driver-facing labels for the buy-now lifecycle. Anything outside
 *  the map (the quote motion, pending) renders the raw status — those
 *  orders are operator-driven and the raw word is still honest. */
const STATUS_LABELS: Partial<Record<MyStoreOrderStatus, string>> = {
  paid: 'Paid',
  processing: 'Being prepared',
  shipped: 'Shipped',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
};

/** "2x AC Elite Home, 1x RFID Cards" — one line per card. */
function itemsSummary(order: MyStoreOrder): string {
  return order.items.map((i) => `${i.quantity}x ${i.name}`).join(', ');
}

/**
 * My Orders (/store/orders). Owner-scoped order history: the server
 * filters on the signed-in buyer, so this page just lists what comes
 * back — number, date, items, total, status, and a tracking line once
 * the order ships.
 */
export function StoreOrdersPage({ onNavigate }: Props) {
  const { t } = useTranslation('storeCheckout');
  const { mode } = useTheme();
  const ordersQ = useQuery({ queryKey: ['my-store-orders'], queryFn: fetchMyOrders, staleTime: 30_000 });
  const orders = ordersQ.data ?? [];

  return (
    <div className={styles.store} data-store-theme={mode}>
      <header className={`${styles.head} ${styles.headSolid}`}>
        <button type="button" className={styles.headBtn} aria-label={t('orders.back')} onClick={() => onNavigate('/store')}>
          <ArrowLeft size={18} />
        </button>
        <span className={`${styles.headTitle} ${styles.headTitleShown}`}>{t('orders.title')}</span>
        <span className={styles.headActions} aria-hidden />
      </header>

      <div className={styles.body}>
        {ordersQ.isLoading && <div className={styles.grid}><div className={styles.skeleton} /></div>}

        {!ordersQ.isLoading && ordersQ.isError && (
          <div className={styles.errorBox}>
            {t('orders.loadError')}
          </div>
        )}

        {!ordersQ.isLoading && !ordersQ.isError && orders.length === 0 && (
          <div className={styles.emptyBox}>
            <p>{t('orders.emptyTitle')}</p>
            <button type="button" className={styles.sheetAdd} onClick={() => onNavigate('/store')}>{t('orders.browse')}</button>
          </div>
        )}

        {!ordersQ.isLoading && !ordersQ.isError && orders.length > 0 && (
          <div className={styles.orderList}>
            {orders.map((order) => (
              <div key={order.id} className={styles.orderCard}>
                <div className={styles.orderTopRow}>
                  <span className={styles.orderNum}>{t('orders.orderNumber', { number: order.order_number })}</span>
                  <span className={styles.orderDate}>{new Date(order.created_at).toLocaleDateString()}</span>
                </div>
                <p className={styles.orderItems}>{itemsSummary(order)}</p>
                <div className={styles.orderFootRow}>
                  <span className={styles.orderTotal}>
                    {formatCents(order.total_cents)} <span className={styles.usd}>USD</span>
                  </span>
                  <span className={styles.orderStatus}>{t(`status.${order.status}`, { defaultValue: STATUS_LABELS[order.status] ?? order.status })}</span>
                </div>
                {order.tracking_number && (
                  <p className={styles.orderTracking}>
                    {order.tracking_carrier ? t('orders.trackingWithCarrier', { carrier: order.tracking_carrier }) : t('orders.tracking')}
                    {order.tracking_number}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
